import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import DrawingSearchPanel from "../DesignerSearchPanel";
import DrawingSnGrid from "../DesignerSnGrid";
import DesignerSnResizable from "../DesignerSnResizable";
import { useReducer, useCallback } from "react";
import DesignerFreeForm from "../DesignerFreeForm";

type PanelSizes = Record<string, string | number>;

interface Action {
  type: "SET_SIZE";
  key: string;
  value: string | number;
}

interface Props {
  loadData?: any;
}

const reducer = (state: PanelSizes, action: Action): PanelSizes => {
  switch (action.type) {
    case "SET_SIZE":
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

const usePanelSizes = (
  defaultSizes: PanelSizes
): [
  PanelSizes,
  (
    seq: number,
    size: string | number,
    options?: { skipStorage?: boolean }
  ) => void,
] => {
  const panelKeys = Object.keys(defaultSizes);

  const initialSizes: PanelSizes = {};
  panelKeys.forEach((key) => {
    initialSizes[key] = defaultSizes[key];
  });

  const [state, dispatch] = useReducer(reducer, initialSizes);

  const resize = useCallback(
    (seq: number, size: string | number) => {
      const key = panelKeys[seq - 1];
      if (!key) return;

      dispatch({ type: "SET_SIZE", key, value: size });
    },
    [panelKeys]
  );

  return [state, resize];
};

/**
 * (상)프리폼, (하)그리드
 */
const TemplateReg5 = forwardRef(({ loadData }: Props, ref) => {
  const gridRef1 = useRef<any>(null);
  const freeformRef = useRef<any>(null);
  const [columns1, setColumns1] = useState<any[]>([{}]);
  const [freeformControl, setFreeformControl] = useState<any[]>([]);
  const [gridSettings1, setGridSettings1] = useState<any[]>([{}]);
  const [gridSettings2, setGridSettings2] = useState<any[]>([{}]);
  const defaultSize = { panel1height: "49.5%", panel2height: "49.5%" };

  const [sizes, onResizeStop] = usePanelSizes(defaultSize);
  useEffect(() => {
    if (!loadData) {
      setColumns1([{}]);
      setFreeformControl([{}]);
    } else {
      setColumns1(loadData.grid.grid1);
      setFreeformControl(loadData.form.freeform1);
      if (loadData.gridSettings?.grid1)
        setGridSettings1(loadData.gridSettings.grid1);
      if (loadData.gridSettings?.grid2)
        setGridSettings2(loadData.gridSettings.grid2);
    }
  }, [loadData]);

  useImperativeHandle(ref, () => ({
    getSaveData,
  }));

  const getSaveData = async () => {
    const freeformFormData = await freeformRef.current.getFormData();
    const freeformControls = await freeformRef.current.getControls();
    const gridSettings1 = await freeformRef.current.getSettings();

    const gridColumns1 = await gridRef1.current.getColumns();
    const gridSettings2 = await gridRef1.current.getSettings();

    const columnsInfo1: any[] = gridSettings1.columnsInfo;
    const columnsInfo2: any[] = gridSettings2.columnsInfo;
    let newDefaultValue1: any = {};
    let newDefaultValue2: any = {};

    const filteredNo = columnsInfo2.filter(
      (col) =>
        col.column_name.includes("no_line") ||
        col.column_name.includes("no_seq")
    );
    filteredNo.map((col) => {
      newDefaultValue2[col.column_name] =
        `grid2ApiRef.current.getMaxValue('${col.column_name}') + 1`;
    });

    columnsInfo1.map((col1) => {
      const filtered = columnsInfo2.filter(
        (col2) => col1.column_name == col2.column_name
      );
      if (filtered.length > 0) {
        if (
          !["am", "um", "qt", "dummy", "reg", "amd", "rt", "rmk"].some(
            (keyword) => col1.column_name.includes(keyword)
          )
        )
          newDefaultValue2[col1.column_name] =
            `formData.${filtered[0].column_name}`;
      }
    });

    const mergedDefaultValue1 = {
      ...gridSettings1.defaultValue,
      ...newDefaultValue1,
    };

    const orderedDefaultValue1: any = {};
    columnsInfo1.forEach((col) => {
      if (mergedDefaultValue1.hasOwnProperty(col.column_name)) {
        orderedDefaultValue1[col.column_name] =
          mergedDefaultValue1[col.column_name];
      }
    });

    const mergedDefaultValue2 = {
      ...gridSettings2.defaultValue,
      ...newDefaultValue2,
    };

    const orderedDefaultValue2: any = {};
    columnsInfo2.forEach((col) => {
      if (mergedDefaultValue2.hasOwnProperty(col.column_name)) {
        orderedDefaultValue2[col.column_name] =
          mergedDefaultValue2[col.column_name];
      }
    });

    const rtn = {
      grid: { grid1: gridColumns1 },
      form: {
        freeform1: freeformControls,
        freeform1ControlData: freeformFormData,
      },
      gridSettings: {
        grid1: {
          ...gridSettings1,
          defaultValue: orderedDefaultValue1,
        },
        grid2: {
          ...gridSettings2,
          defaultValue: orderedDefaultValue2,
        },
      },
    };
    return rtn;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 space-y-1.5">
        <DesignerSnResizable
          direction={"height"}
          seq={1}
          size={sizes.panel1height}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          minHeight={130}
        >
          <DesignerFreeForm
            type={"프리폼"}
            ref={freeformRef}
            data={{}}
            controls={freeformControl}
            settingsData={gridSettings1}
          />
        </DesignerSnResizable>
        <DesignerSnResizable
          direction={"height"}
          seq={2}
          size={sizes.panel2height}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          minHeight={130}
        >
          <DrawingSnGrid
            ref={gridRef1}
            seq={1}
            enableAddRow={true}
            columnConfig={columns1}
            rowData={[]}
            title="타이틀"
            settingsData={gridSettings2}
          />
        </DesignerSnResizable>
      </div>
    </div>
  );
});

export default TemplateReg5;
